import axios from 'axios'

// Создаем экземпляр axios с базовой конфигурацией
const api = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
})

// CSRF токен
let csrfToken = null

// Функция для получения CSRF токена
const getCSRFToken = async () => {
    if (!csrfToken) {
        try {
            const response = await axios.get('/api/csrf-token')
            csrfToken = response.data.data.token
            // Сохраняем название заголовка для использования в interceptor
            window.csrfHeaderName = response.data.data.header || 'X-CSRF-Token'
        } catch (error) {
            console.warn('Failed to get CSRF token:', error)
        }
    }
    return csrfToken
}

// Interceptor для запросов - добавляем токен авторизации и CSRF
api.interceptors.request.use(
    async (config) => {
        // Добавляем токен авторизации
        const token = localStorage.getItem('auth_token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }

        // Добавляем CSRF токен для изменяющих запросов
        if (['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase())) {
            const token = await getCSRFToken()
            if (token) {
                // Используем название заголовка из ответа сервера
                const headerName = window.csrfHeaderName || 'X-CSRF-Token'
                config.headers[headerName] = token
            }
        }

        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Interceptor для ответов - обработка ошибок
api.interceptors.response.use(
    (response) => {
        return response.data
    },
    async (error) => {
        // Обработка различных типов ошибок
        const errorResponse = {
            message: 'Произошла неизвестная ошибка',
            status: error.response?.status || 0,
            data: error.response?.data,
            type: 'unknown'
        }

        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            // Ошибка таймаута
            errorResponse.message = 'Превышено время ожидания ответа от сервера'
            errorResponse.type = 'timeout'
        } else if (!error.response) {
            // Ошибка сети
            errorResponse.message = 'Ошибка подключения к серверу. Проверьте интернет-соединение'
            errorResponse.type = 'network'
        } else {
            // Ошибки от сервера
            const status = error.response.status
            const serverMessage = error.response?.data?.error?.message || error.response?.data?.message

            switch (status) {
                case 400:
                    errorResponse.message = serverMessage || 'Неверный запрос'
                    errorResponse.type = 'validation'
                    break
                case 401:
                    errorResponse.message = 'Необходима авторизация'
                    errorResponse.type = 'auth'
                    // Перенаправляем на страницу входа
                    localStorage.removeItem('auth_token')
                    if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
                        window.location.href = '/admin/login'
                    }
                    break
                case 403:
                    // Проверяем, не CSRF ли это ошибка
                    if (error.response?.data?.error?.type === 'csrf_error') {
                        // Сбрасываем токен и пытаемся получить новый
                        csrfToken = null
                        delete api.defaults.headers.common['X-CSRF-Token']

                        // Если это не повторная попытка, пробуем еще раз
                        if (!error.config._retry) {
                            error.config._retry = true
                            await getCSRFToken()
                            return api.request(error.config)
                        }
                        errorResponse.message = 'Ошибка безопасности. Попробуйте еще раз'
                        errorResponse.type = 'csrf'
                    } else {
                        errorResponse.message = serverMessage || 'Недостаточно прав для выполнения операции'
                        errorResponse.type = 'forbidden'
                    }
                    break
                case 404:
                    errorResponse.message = serverMessage || 'Запрашиваемый ресурс не найден'
                    errorResponse.type = 'not_found'
                    break
                case 422:
                    errorResponse.message = serverMessage || 'Ошибка валидации данных'
                    errorResponse.type = 'validation'
                    // Добавляем детали валидации
                    if (error.response.data?.errors) {
                        errorResponse.validationErrors = error.response.data.errors
                    }
                    break
                case 429:
                    const retryAfter = error.response.headers['retry-after']
                    if (error.response?.data?.error?.type === 'ip_blocked') {
                        errorResponse.message = 'IP адрес временно заблокирован из-за подозрительной активности'
                        errorResponse.type = 'ip_blocked'
                    } else {
                        const message = retryAfter
                            ? `Слишком много запросов. Попробуйте через ${retryAfter} секунд`
                            : 'Слишком много запросов. Попробуйте позже'
                        errorResponse.message = serverMessage || message
                        errorResponse.type = 'rate_limit'
                    }
                    break
                case 500:
                    errorResponse.message = 'Внутренняя ошибка сервера'
                    errorResponse.type = 'server'
                    break
                case 502:
                case 503:
                case 504:
                    errorResponse.message = 'Сервер временно недоступен. Попробуйте позже'
                    errorResponse.type = 'server_unavailable'
                    break
                default:
                    errorResponse.message = serverMessage || `Ошибка сервера (${status})`
                    errorResponse.type = 'server'
            }
        }

        // Логируем ошибку в консоль для разработки
        if (import.meta.env.NODE_ENV === 'development') {
            console.error('API Error:', {
                url: error.config?.url,
                method: error.config?.method,
                status: errorResponse.status,
                message: errorResponse.message,
                originalError: error
            })
        }

        return Promise.reject(errorResponse)
    }
)

// API для аутентификации
export const authApi = {
    login: (credentials) => api.post('/auth/login', credentials),
    logout: () => api.post('/auth/logout'),
    me: () => api.get('/auth/me'),
    changePassword: (passwordData) => api.put('/auth/password', passwordData),
    refresh: () => api.post('/auth/refresh'),
}

// API для проектов
export const projectsApi = {
    getAll: (params) => api.get('/projects', { params }),
    getById: (id) => api.get(`/projects/${id}`),
    create: (data) => api.post('/projects', data),
    update: (id, data) => api.put(`/projects/${id}`, data),
    delete: (id) => api.delete(`/projects/${id}`),
    uploadImage: (id, file) => {
        const formData = new FormData()
        formData.append('image', file)
        return api.post(`/projects/${id}/images`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
}

// API для навыков
export const skillsApi = {
    getAll: () => api.get('/skills'),
    create: (data) => api.post('/skills', data),
    update: (id, data) => api.put(`/skills/${id}`, data),
    delete: (id) => api.delete(`/skills/${id}`),
    updateOrder: (skillsOrder) => api.put('/skills/order', { skills: skillsOrder }),
}

// API для профиля
export const profileApi = {
    get: () => api.get('/profile'),
    update: (data) => api.put('/profile', data),
    uploadAvatar: (file) => {
        const formData = new FormData()
        formData.append('avatar', file)
        return api.post('/profile/avatar', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    updateSocialLinks: (socialLinks) => api.put('/profile/social-links', { socialLinks }),
}

// API для контактов
export const contactApi = {
    sendMessage: (data) => api.post('/contact', data),
    getMessages: (params) => api.get('/contact/messages', { params }),
    markAsRead: (id) => api.put(`/contact/messages/${id}`),
    deleteMessage: (id) => api.delete(`/contact/messages/${id}`),
    getContactInfo: () => api.get('/contact/info'),
}

// API для медиафайлов
export const mediaApi = {
    upload: (file, type = 'image') => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('type', type)
        return api.post('/media/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    delete: (filename) => api.delete(`/media/${filename}`),
    getUrl: (filename) => `/api/media/${filename}`,
}

// API для уведомлений
export const notificationsApi = {
    getAll: (params) => api.get('/notifications', { params }),
    markAsRead: (id) => api.put(`/notifications/${id}/read`),
    markAllAsRead: () => api.put('/notifications/read-all'),
    delete: (id) => api.delete(`/notifications/${id}`),
}

// API для резюме
export const resumeApi = {
    // Получение всех данных резюме
    getAll: () => api.get('/resume'),
    
    // Опыт работы
    getExperience: () => api.get('/resume/experience'),
    createExperience: (data) => api.post('/resume/experience', data),
    updateExperience: (id, data) => api.put(`/resume/experience/${id}`, data),
    deleteExperience: (id) => api.delete(`/resume/experience/${id}`),
    
    // Образование
    getEducation: () => api.get('/resume/education'),
    createEducation: (data) => api.post('/resume/education', data),
    updateEducation: (id, data) => api.put(`/resume/education/${id}`, data),
    deleteEducation: (id) => api.delete(`/resume/education/${id}`),
    
    // Сертификаты
    getCertificates: () => api.get('/resume/certificates'),
    createCertificate: (data) => api.post('/resume/certificates', data),
    updateCertificate: (id, data) => api.put(`/resume/certificates/${id}`, data),
    deleteCertificate: (id) => api.delete(`/resume/certificates/${id}`),
    
    // Языки
    getLanguages: () => api.get('/resume/languages'),
    createLanguage: (data) => api.post('/resume/languages', data),
    updateLanguage: (id, data) => api.put(`/resume/languages/${id}`, data),
    deleteLanguage: (id) => api.delete(`/resume/languages/${id}`),
    
    // Достижения
    getAchievements: () => api.get('/resume/achievements'),
    createAchievement: (data) => api.post('/resume/achievements', data),
    updateAchievement: (id, data) => api.put(`/resume/achievements/${id}`, data),
    deleteAchievement: (id) => api.delete(`/resume/achievements/${id}`),
    
    // Экспорт в PDF
    exportToPDF: () => api.get('/resume/export/pdf', { responseType: 'blob' }),
}

// Утилиты для работы с API
export const apiUtils = {
    // Обработка ошибок валидации
    handleValidationErrors: (error) => {
        if (error.status === 422 && error.data?.errors) {
            return error.data.errors
        }
        return {}
    },

    // Создание URL с параметрами
    buildUrl: (endpoint, params = {}) => {
        const url = new URL(endpoint, window.location.origin)
        Object.keys(params).forEach(key => {
            if (params[key] !== null && params[key] !== undefined) {
                url.searchParams.append(key, params[key])
            }
        })
        return url.toString()
    },

    // Проверка успешности ответа
    isSuccess: (response) => {
        return response && response.success === true
    },

    // Извлечение сообщения об ошибке
    getErrorMessage: (error) => {
        return error?.message || 'Произошла неизвестная ошибка'
    },

    // Получение CSRF токена
    getCSRFToken: () => getCSRFToken(),

    // Сброс CSRF токена (для принудительного обновления)
    resetCSRFToken: () => {
        csrfToken = null
        // delete api.defaults.headers.common['X-CSRF-Token']
    },

    // Проверка типа ошибки безопасности
    isSecurityError: (error) => {
        return ['csrf', 'rate_limit', 'ip_blocked', 'forbidden'].includes(error.type)
    },
}

export default api